// Extensie locală (nu există în wingo): legătura dintre lib/navigation și react-router-dom v6.
// Componentele wingo randează link-urile prin NavLink → `component` (din prop `linkComponent` sau din LinkProvider),
// cu props de <a> și `href`. React Router vrea `to`, deci RouterLink traduce href → to; URL-urile externe (mailto:,
// tel:, alt domeniu) și cele cu target rămân <a> simple.
//
// O singură dată, în interiorul <BrowserRouter> (are nevoie de useLocation / useNavigate):
//   <RouterLinkProvider>{routes}</RouterLinkProvider>
// sau punctual: <EntityHeader linkComponent={RouterLink} … />
import { forwardRef, useCallback, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isExternalHref, LinkProvider, type LinkComponent, type LinkComponentProps } from "@/components/wingo/lib/navigation";

const RouterLinkInner = forwardRef<HTMLAnchorElement, Omit<LinkComponentProps, "ref">>(function RouterLink(
  { href, target, ...props },
  ref,
) {
  if (isExternalHref(href) || (target && target !== "_self") || props.download !== undefined) {
    return <a ref={ref} href={href} target={target} {...props} />;
  }
  return <Link ref={ref} to={href} target={target} {...props} />;
});

export const RouterLink = RouterLinkInner as unknown as LinkComponent;

export type RouterLinkProviderProps = {
  // rulează la fiecare navigare internă pornită dintr-un NavLink (ex. o bară de progres)
  onNavigate?: (href: string) => void;
  children?: ReactNode;
};

// LinkProvider legat de router: link-urile trec prin <Link>, pagina curentă vine din useLocation (elementul activ din
// breadcrumb / nav), iar useNavigate() din wingo (navigări fără ancoră) folosește navigate-ul din react-router
export function RouterLinkProvider({ onNavigate, children }: RouterLinkProviderProps) {
  const { pathname } = useLocation();
  const routerNavigate = useNavigate();
  const navigate = useCallback((href: string) => routerNavigate(href), [routerNavigate]);
  return (
    <LinkProvider component={RouterLink} currentPath={pathname} navigate={navigate} onNavigate={onNavigate}>
      {children}
    </LinkProvider>
  );
}
